import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import omit from "lodash/omit";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import {
  EnvironmentHeaderCollectionQuerySchema,
  EnvironmentHeaderDTO,
} from "@reflag/shared/environmentAPI";
import { z } from "@reflag/shared/utils/zod";

import { environmentQueryKeys } from "./environmentQueryKeys";

type EnvironmentHeaderCollectionQuery = z.input<
  typeof EnvironmentHeaderCollectionQuerySchema
>;

export const useEnvironmentsData = <TData = EnvironmentHeaderDTO[]>(
  searchParams?: EnvironmentHeaderCollectionQuery & { appId?: string },
  queryOptions?: Omit<
    UseQueryOptions<EnvironmentHeaderDTO[], AxiosError<ErrorResponse>, TData>,
    "queryKey" | "queryFn" | "initialData"
  > & { initialData?: () => undefined },
) => {
  const { appId: currentAppId } = useEnvironment();

  // Avoid literal undefined as it becomes the string 'undefined' in search params
  const cleanSearchParams: EnvironmentHeaderCollectionQuery | undefined =
    searchParams &&
    Object.fromEntries(
      Object.entries(omit(searchParams, "appId")).filter(([_key, value]) => {
        return typeof value !== "undefined";
      }),
    );

  const appId = searchParams?.appId ?? currentAppId;

  const dataUrl = `/apps/${appId}/environments?${new URLSearchParams(
    cleanSearchParams as Record<string, string>,
  ).toString()}`;

  return useQuery({
    queryKey: environmentQueryKeys.list(appId, cleanSearchParams),

    queryFn: () =>
      api
        .get<"/apps/:appId/environments">(dataUrl)
        .then((res) => res.data.data),
    enabled: !!appId,
    ...queryOptions,
  });
};
